import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";
import PropTypes from "prop-types";
import { analysisClient } from "src/services/requestClient";
import { HOLDER } from "src/config/constants";

const AnalysisContext = React.createContext({});

const AnalysisProvider = ({ children }) => {
  const { address } = useWeb3Connect();
  const [statistic, setStatistic] = useState({});
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);

  const fetchStatistic = async (holder, pinWidth = 100, numberWallet = 5) => {
    let rangeScore = "";
    let scoresHistogram = "";
    let topScores = "";
    let topIncreasing = "";
    let topAverage = "";
    if (holder === HOLDER.OVERALL) {
      rangeScore = "/range_score";
      scoresHistogram = `/scoreshistogram?pin_width=${pinWidth}`;
      topScores = `/topscores?number=${numberWallet}`;
      topIncreasing = `/topincreasing?number=${numberWallet}`;
      topAverage = `/topaverage?number=${numberWallet}`;
    }
    if (holder !== HOLDER.OVERALL) {
      rangeScore = `/range_score?holding_token=${holder}`;
      scoresHistogram = `/scoreshistogram?pin_width=${pinWidth}&holding_token=${holder}`;
      topScores = `/topscores?number=${numberWallet}&holding_token=${holder}`;
      topIncreasing = `/topincreasing?number=${numberWallet}&holding_token=${holder}`;
      topAverage = `/topaverage?number=${numberWallet}&holding_token=${holder}`;
    }
    let [_rangeScore, _scoresHistogram, _topScores, _topIncreasing, _topAverage] = await Promise.all([
      analysisClient.get(rangeScore),
      analysisClient.get(scoresHistogram),
      analysisClient.get(topScores),
      analysisClient.get(topIncreasing),
      analysisClient.get(topAverage),
    ]);
    _rangeScore = _rangeScore.data;
    _scoresHistogram = _scoresHistogram.data;
    _topScores = _topScores.data;
    _topIncreasing = _topIncreasing.data.map((element) => {
      return {
        address: element["address"],
        score: element["average_increasing"],
      };
    });
    _topAverage = _topAverage.data.map((element) => {
      return {
        address: element["address"],
        score: element["average_scores"],
      };
    });
    return {
      rangeScore: _rangeScore,
      scoreHistogram: _scoresHistogram,
      topData: {
        topScore: _topScores,
        topIncreasing: _topIncreasing,
        topAverage: _topAverage,
      },
    };
  };

  const fetchAccountStats = async (accountAddress) => {
    const data = await analysisClient.get(`/stats/${accountAddress}`);
    return data.data;
  };

  const fetchStatisticData = useCallback(async (holder) => {
    if (!statistic[holder]) {
      try {
        let data = await fetchStatistic(holder);
        let _statistic = statistic;
        _statistic[holder] = data;
        setStatistic({ ..._statistic });
      } catch (error) {
        console.debug("analysis error", error);
        setError(error);
      }
    }
  });

  const fetchAccountData = useCallback(async () => {
    try {
      if (address) {
        let account = await fetchAccountStats(address);
        const textLevel = account["level"];
        let rank = 0;
        if (textLevel === "Conqueror") rank = 1;
        else if (textLevel === "Hero") rank = 2;
        else if (textLevel === "Master") rank = 3;
        else if (textLevel === "Legendary") rank = 4;
        account["rank"] = rank;
        setAccount(account);
      } else {
        setAccount(null);
      }
    } catch (error) {
      console.debug("analysis error", error);
      setError(error);
    }
  }, [address]);

  useEffect(() => {
    fetchAccountData();
  }, [address]);

  const contextData = useMemo(() => {
    return {
      error,
      data: {
        statistic,
        account,
      },
      fetch: {
        fetchStatisticData,
      },
    };
  }, [error, statistic, account, fetchAccountData]);

  return <AnalysisContext.Provider value={contextData}>{children}</AnalysisContext.Provider>;
};

AnalysisProvider.propTypes = {
  children: PropTypes.node,
};

export default AnalysisProvider;

export const useAnalysisContext = () => React.useContext(AnalysisContext);

export const useAnalysisSelector = (selectorFn) => {
  const { data } = useAnalysisContext();

  return useMemo(() => selectorFn(data), [data, selectorFn]);
};
